import { useState } from "react";
import { motion } from "framer-motion";
import { Calculator } from "lucide-react";
import { FadeUp, Eyebrow, SectionShell, EASE } from "./Reveal";
import { RATES } from "../constants/site";

const HOURLY_LOW = 8;
const HOURLY_HIGH = 15;
const HOURLY_MID = (HOURLY_LOW + HOURLY_HIGH) / 2;
const US_HOURLY_ASSUMPTION = 55;

export default function RateSheet() {
  const [hours, setHours] = useState(40);

  const taccLow = hours * HOURLY_LOW;
  const taccHigh = hours * HOURLY_HIGH;
  const inHouse = hours * US_HOURLY_ASSUMPTION;
  const savings = Math.round((1 - HOURLY_MID / US_HOURLY_ASSUMPTION) * 100);

  const fmt = (n) => `$${n.toLocaleString("en-US")}`;

  return (
    <SectionShell id="rates" className="py-24 sm:py-32">
      <FadeUp>
        <Eyebrow>Indicative Rate Sheet</Eyebrow>
        <h2 className="mt-5 max-w-2xl font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.08] text-ink">
          Transparent pricing. <span className="text-gradient">No hidden fees.</span>
        </h2>
      </FadeUp>

      <div className="mt-14 grid lg:grid-cols-12 gap-8 items-start">
        <FadeUp className="lg:col-span-7" delay={0.05}>
          <div data-testid="rate-sheet-table" className="overflow-hidden rounded-3xl border border-line bg-card shadow-soft">
            {RATES.map((r, i) => (
              <motion.div
                key={r.service}
                initial={{ opacity: 0, x: -18 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.5, delay: i * 0.035, ease: EASE }}
                className={`group flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-6 px-6 sm:px-7 py-4.5 sm:py-5 transition-colors duration-300 hover:bg-paper ${
                  i !== RATES.length - 1 ? "border-b border-line" : ""
                }`}
              >
                <div className="sm:w-[38%]">
                  <p className="text-sm font-bold text-ink">{r.service}</p>
                  <p className="text-xs text-slate-400">{r.desc}</p>
                </div>
                <p className="sm:flex-1" />
                <p className="font-mono text-sm sm:text-right text-torange group-hover:text-bluedark transition-colors duration-300 whitespace-nowrap">
                  {r.rate}
                </p>
              </motion.div>
            ))}
          </div>
          <p className="mt-4 font-mono text-[11px] text-slate-400">Indicative rates as published by TaccTax Solution Pvt. Ltd. Final quotes depend on scope and volume.</p>
        </FadeUp>

        <FadeUp delay={0.15} className="lg:col-span-5 lg:sticky lg:top-28">
          <div data-testid="rate-calculator" className="relative overflow-hidden rounded-3xl border border-line bg-card p-7 sm:p-9 shadow-lift">
            <div className="absolute -top-20 -right-20 h-52 w-52 rounded-full bg-torange/15 blur-3xl" />
            <div className="flex items-center gap-3">
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-bluedark to-torange shadow-soft">
                <Calculator size={20} className="text-white" />
              </div>
              <h3 className="font-display text-xl font-bold text-ink">Savings Estimator</h3>
            </div>

            <label className="mt-9 block">
              <div className="flex items-baseline justify-between mb-4">
                <span className="text-sm font-semibold text-slate-700">Monthly bookkeeping hours</span>
                <span data-testid="rate-calc-hours-value" className="font-mono text-lg font-bold text-gradient">{hours} hrs</span>
              </div>
              <input
                data-testid="rate-calc-hours-slider"
                type="range"
                min="10"
                max="160"
                step="5"
                value={hours}
                onChange={(e) => setHours(Number(e.target.value))}
                className="w-full cursor-pointer"
              />
            </label>

            <div className="mt-8 space-y-4" data-testid="rate-calc-output">
              <div className="rounded-2xl border border-line bg-paper p-5">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">With TaccTax</p>
                <p className="mt-2 font-display text-2xl sm:text-3xl font-extrabold text-gradient">
                  {fmt(taccLow)} – {fmt(taccHigh)} <span className="text-sm font-semibold text-slate-400">/ mo</span>
                </p>
              </div>
              <div className="rounded-2xl border border-line bg-paper p-5">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">In-house equivalent*</p>
                <p className="mt-2 font-display text-2xl sm:text-3xl font-extrabold text-slate-400">
                  ~{fmt(inHouse)} <span className="text-sm font-semibold text-slate-400">/ mo</span>
                </p>
              </div>
            </div>

            <div className="mt-6 rounded-2xl bg-gradient-to-r from-bluedark/10 to-torange/15 border border-torange/30 p-5 text-center">
              <p className="text-sm text-slate-600">
                Estimated saving on bookkeeping payroll
              </p>
              <p data-testid="rate-calc-savings" className="mt-1 font-display text-4xl font-extrabold text-gradient">
                ~{savings}%
              </p>
            </div>

            <p className="mt-5 text-[11px] leading-relaxed text-slate-400">
              *Assumes ~$55/hr blended US in-house cost — an indicative industry
              assumption for comparison only. Tax returns, payroll and vCFO work
              are quoted per the rate sheet.
            </p>
          </div>
        </FadeUp>
      </div>
    </SectionShell>
  );
}
